import { useCallback, useEffect, useMemo, useState } from "react"
import { useGetMyScheduleQuery, useGetStaffScheduleQuery } from "../bookingCalendarApi"
import type { BookingDetails, StaffScheduleSite, StaffScheduleStatus } from "../bookingCalendarApi"
import { SITE_TIME_ZONE, weekDatesFrom, weekWindow, zonedDateAndMinutes } from "./siteTime"
import type { CalendarDate, WeekDate } from "./siteTime"
import { bookingsOutsideWindow, businessHoursFor, isLiveBooking } from "./dayGrid"
import type { DayLoadStatus, UnavailabilityMark } from "./dayGrid"
import { buildWeekGrid, unavailabilityByDay, weekAvailability, weekSlotMinutes } from "./weekGrid"
import type { WeekSchedule } from "./weekGrid"
import { awaitingCurrent, useTrackedRefresh } from "./viewLoading"
import type { BookingSeasonBounds } from "./bookingSeason"
import { firstCalThrottled } from "@/lib/calThrottled"

// Stable identities while the query is loading: a fresh [] each render would give every memo below
// a new identity and rebuild the whole week on any unrelated render.
const NO_BOOKINGS: BookingDetails[] = []
const NO_SITES: StaffScheduleSite[] = []

/** Monday to Friday. The offices take appointments on weekdays; a weekend booking is counted, not drawn. */
const WEEKDAYS_SHOWN = 5

export interface StaffWeek {
  /** The five days on the grid, Monday first. */
  weekDates: WeekDate[]
  /** Every site this person hosts at — what the sidebar lists, whichever one is in view. */
  sites: StaffScheduleSite[]
  /** The site the week is a week of, or undefined when it is all of them at once. */
  site: StaffScheduleSite | undefined
  schedule: WeekSchedule
  /** The zone every time on the grid is resolved against: the site in view's, never the browser's. */
  timeZone: string
  /** Row height, and the length of an ordinary appointment. The shortest across the sites in view. */
  slotMinutes: number
  /** Live (non-cancelled) appointments they host this week at the sites in view. Zero while `slotsLoading`. */
  bookingCount: number
  /** Live appointments per site, keyed by Cal team id — the counts beside the sidebar's entries. */
  bookingCountBySite: Record<number, number>
  /** Of the ones in view, those with no row on the grid: at the weekend, or outside office hours. */
  outsideWindowCount: number
  /**
   * `bookingCount` and `outsideWindowCount` for each day, keyed by the site-local date the
   * appointment starts on — what the day view's toolbar counts (AB#766). Days with none are absent.
   */
  bookingCountByDay: Record<CalendarDate, number>
  outsideWindowCountByDay: Record<CalendarDate, number>
  /** Days they are not working, keyed by ISO date. Absent means bookable all day. */
  dayUnavailability: Record<CalendarDate, UnavailabilityMark>
  /**
   * The sites in view whose availability could not be read, by name. Empty when every one answered.
   *
   * While this holds anything the grid draws no shading at all — see `weekAvailability` — so it is
   * over-stating availability, and the page has to say so. Distinct from `error`: the week loaded
   * and is worth showing.
   */
  unavailableSites: string[]
  /**
   * "loading" only while there is nothing to cut a grid from — no sites, from this week or any other.
   * A week arriving after one already seen is `slotsLoading` instead.
   */
  status: DayLoadStatus
  /**
   * The grid's shape is known but this week's contents are not: the week is still on its way, or the
   * user has asked for it again. The grid draws its cells as placeholders, and the counts above read
   * as nothing booked until it lands.
   */
  slotsLoading: boolean
  error: string | null
  /**
   * The week's read when Cal throttled it (503 `calThrottled`, AB#514), or null. The page shows the
   * busy notice for it in place of `error`, with `refresh` as its retry.
   */
  throttledError: unknown
  /**
   * Why there is no week to draw: no Cal account, or a Cal account on no team. Both are onboarding
   * gaps rather than quiet weeks, and both need saying rather than showing as an empty grid.
   */
  resolution: StaffScheduleStatus
  /**
   * Whose week this is, as a Cal user id. Undefined until the schedule loads.
   *
   * Resolved by the server rather than passed in, because the own-week case arrives with no id at all
   * — the signed-in staff member is identified from their token — and booking into one of these slots
   * has to name a host.
   */
  calUserId: number | undefined
  /**
   * Re-read the week from the server, cache or no cache.
   *
   * Cal is written to from outside this app — by staff in Cal itself, and by households booking on
   * the public site — so a week left open goes stale without anything here changing to invalidate
   * it. This is the way back to the truth without reloading the page.
   */
  refresh: () => void
  /**
   * A read is in flight, including a refetch of data already on screen — what spins the refresh
   * button. Wider than `slotsLoading`: a refetch after this app's own write keeps the grid up.
   */
  isFetching: boolean
}

/**
 * One staff member's week, at one of their sites or across all of them.
 *
 * Everything comes from one endpoint, and which endpoint is the whole of the difference between the
 * two people who can be looking at this. With no `calUserId` it is the signed-in staff member's own
 * week, from an endpoint that decides whose bookings these are from the token and cannot be asked
 * for anyone else's. With one it is a named colleague's, from the endpoint behind
 * `appointments:manage` — the narrowing is still the server's, because these appointments carry
 * attendee names and phone numbers either way.
 *
 * `selectedSiteId` picks among what comes back; it is not part of either request.
 *
 * @param staffCalUserId The staff member to look at, by Cal account. Null or undefined for the
 * caller's own week. Only an administrator can pass one — the server refuses the rest.
 * @param bookingSeason When the public may book, to mark the rows they may not. Read by the page
 * rather than here, and undefined while it is unknown — which marks nothing.
 */
export function useStaffWeek(
  anchor: CalendarDate,
  selectedSiteId: number | null,
  staffCalUserId?: number | null,
  bookingSeason?: BookingSeasonBounds,
): StaffWeek {
  // The zone the week is bracketed in. It has to be the zone the grid buckets bookings by, or the
  // window and the grid disagree about which days they are talking about — the week is asked for in
  // one clock and drawn on another, and the appointments at either end fall through the gap.
  //
  // Which zone that is only arrives with the sites themselves, so the deployment's stands in until
  // the first response, and a site in another zone widens to its own and refetches once. Held in
  // state rather than read off the response, because it is the response's own argument: the effect
  // below is what closes that loop, and it settles because the second response resolves the same
  // sites as the first.
  const [windowZone, setWindowZone] = useState(SITE_TIME_ZONE)

  const window = useMemo(() => weekWindow(anchor, windowZone), [anchor, windowZone])

  // Both hooks are called and one of them is always skipped: which endpoint answers depends on
  // whether a colleague was named, and a hook cannot be called conditionally. A skipped query holds
  // no subscription and makes no request, so this costs nothing but the two lines.
  const viewingSomeoneElse = staffCalUserId != null
  const myScheduleQuery = useGetMyScheduleQuery(window, { skip: viewingSomeoneElse })
  const staffScheduleQuery = useGetStaffScheduleQuery(
    { calUserId: staffCalUserId ?? 0, ...window },
    { skip: !viewingSomeoneElse })

  const scheduleQuery = viewingSomeoneElse ? staffScheduleQuery : myScheduleQuery

  // This week, and nothing but this week, is what the grid paints: `data` is the last week the hook
  // held, so stepping forward kept drawing the previous week's appointments under the new dates
  // until the new ones landed.
  const current = scheduleQuery.currentData

  // What the grid is cut from while this week is on its way: the last week seen, if it was this
  // person's. Their sites, hours and slot length do not change from one week to the next, so the
  // sidebar and the rows hold still across the step instead of collapsing to a skeleton and back.
  // Never its appointments or availability — those are the week's, and come from `current` alone.
  const previous = scheduleQuery.data
  const shape = current
    ?? (previous && (!viewingSomeoneElse || previous.calUserId === staffCalUserId) ? previous : undefined)

  const shapeSites = shape?.sites ?? NO_SITES
  const calUserId = shape?.calUserId

  // An id naming a site they no longer host at falls back to all of them rather than to an empty
  // week: the sidebar entry it referred to is gone from the list as well.
  const shapeSite = useMemo(
    () => (selectedSiteId == null ? undefined : shapeSites.find(s => s.teamId === selectedSiteId)),
    [shapeSites, selectedSiteId])

  // The same sites, as this week reports them — the ones holding this week's appointments and
  // availability. Empty until it arrives, which draws nothing booked and nobody away; the page puts
  // placeholders over that rather than letting it read as an answer.
  const sites = current?.sites ?? NO_SITES
  const otherBookings = current?.otherBookings ?? NO_BOOKINGS
  const site = useMemo(
    () => (shapeSite == null ? undefined : sites.find(s => s.teamId === shapeSite.teamId)),
    [sites, shapeSite])

  const inView = useMemo(() => (site ? [site] : sites), [site, sites])
  const shapeInView = useMemo(() => (shapeSite ? [shapeSite] : shapeSites), [shapeSite, shapeSites])

  // Appointments on an event type no site claims are part of "all my sites" and part of no single
  // one — the same place they sit in the response.
  const bookings = useMemo(
    () => (site ? site.bookings : [...sites.flatMap(s => s.bookings), ...otherBookings]),
    [site, sites, otherBookings])

  const timeZone = shapeSite?.timeZone || shapeSites[0]?.timeZone || SITE_TIME_ZONE

  useEffect(() => { setWindowZone(timeZone) }, [timeZone])

  const weekDates = useMemo(() => weekDatesFrom(anchor).slice(0, WEEKDAYS_SHOWN), [anchor])

  const slotMinutes = useMemo(() => weekSlotMinutes(shapeInView), [shapeInView])

  // Undefined while unknown, and undefined too when any site in view failed to answer: the grid
  // reads a list as the whole truth and would shade a site's real working hours as time off.
  const availability = useMemo(
    () => (calUserId == null ? undefined : weekAvailability(inView, calUserId)),
    [inView, calUserId])

  // The off-slot event types of the sites in view, so a free row can say which kind of appointment
  // it would take. Across the sites in view rather than per site, because that is the shape the
  // availability union above already has.
  const offSlotEventTypeIds = useMemo(() => inView.flatMap(s => s.offSlotEventTypeIds), [inView])
  // And the private ones (AB#779). Absent from an older API, which leaves every row public or managed.
  const privateEventTypeIds = useMemo(() => inView.flatMap(s => s.privateEventTypeIds ?? []), [inView])

  // An outreach site closes at noon, so a week of nothing but outreach sites draws the morning only.
  // Read off the shape rather than this week's sites, so the rows hold still across a step.
  const businessHours = useMemo(() => businessHoursFor(shapeInView.map(s => ({ slug: s.teamSlug, isMainOffice: s.isMainOffice, gridWindow: s.gridWindow }))), [shapeInView])

  const gridInput = useMemo(
    () => ({
      weekDates,
      bookings,
      calUserId: calUserId ?? 0,
      slotMinutes,
      timeZone,
      availability,
      offSlotEventTypeIds,
      privateEventTypeIds,
      businessHours,
      bookingSeason,
    }),
    [weekDates, bookings, calUserId, slotMinutes, timeZone, availability, offSlotEventTypeIds, privateEventTypeIds, businessHours, bookingSeason])

  const schedule = useMemo(() => buildWeekGrid(gridInput), [gridInput])
  const dayUnavailability = useMemo(() => unavailabilityByDay(gridInput), [gridInput])

  // Appointments the grid has no row for: at the weekend, which this view does not draw, or outside
  // the office hours it does. Counted rather than quietly dropped — they are still this person's
  // appointments, and a week that loses one silently is worse than one that says it has.
  const undrawn = useMemo(() => {
    const drawnDays = new Set(weekDates.map(day => day.isoDate))

    const offWeekend = bookings.filter(booking =>
      isLiveBooking(booking) && !drawnDays.has(zonedDateAndMinutes(booking.start, timeZone).date))

    const offHours = weekDates.flatMap(day => bookingsOutsideWindow({
      date: day.isoDate,
      bookings,
      columns: [{ id: "me", calUserId: calUserId ?? 0 }],
      slotMinutes,
      timeZone,
      businessHours,
    }))

    return [...offWeekend, ...offHours]
  }, [weekDates, bookings, calUserId, slotMinutes, timeZone, businessHours])

  // A count in the toolbar says something is off; it does not say which appointments, and these are
  // the ones worth chasing. Logged for the same reason the day view logs its own.
  useEffect(() => {
    if (undrawn.length === 0) return
    console.error(
      `Staff week: ${undrawn.length} appointment(s) fall outside the grid and cannot be drawn:`,
      undrawn.map(booking => ({ uid: booking.uid, start: booking.start, end: booking.end })))
  }, [undrawn])

  const bookingCountByDay = useMemo(
    () => countByDay(bookings.filter(isLiveBooking), timeZone),
    [bookings, timeZone])
  const outsideWindowCountByDay = useMemo(() => countByDay(undrawn, timeZone), [undrawn, timeZone])

  const bookingCountBySite = useMemo(() => {
    const counts: Record<number, number> = {}
    for (const s of sites) counts[s.teamId] = s.bookings.filter(isLiveBooking).length
    return counts
  }, [sites])

  const { refreshing, track: trackRefresh } =
    useTrackedRefresh(`${staffCalUserId ?? "me"}:${window.start}`)

  // One read is the whole week — the sites, their hours, the availability and the bookings all
  // arrive together — so unlike the day grid there is nothing to refetch alongside it. The other
  // endpoint is the skipped one and has never run, which is why this goes through `scheduleQuery`
  // rather than both: refetching an uninitialized query warns and does nothing.
  const refresh = useCallback(() => {
    if (!scheduleQuery.isUninitialized) trackRefresh([scheduleQuery.refetch()])
  }, [scheduleQuery, trackRefresh])

  const loadStatus: DayLoadStatus =
    scheduleQuery.error ? "failed"
      : awaitingCurrent(scheduleQuery) && !shape ? "loading"
        : current ? "succeeded"
          : "idle"

  return {
    weekDates,
    sites: shapeSites,
    site: shapeSite,
    schedule,
    timeZone,
    slotMinutes,
    bookingCount: bookings.filter(isLiveBooking).length,
    bookingCountBySite,
    outsideWindowCount: undrawn.length,
    bookingCountByDay,
    outsideWindowCountByDay,
    dayUnavailability,
    unavailableSites: inView.filter(s => s.availabilityUnavailable).map(s => s.teamName),
    status: loadStatus,
    slotsLoading: !scheduleQuery.error && (refreshing || awaitingCurrent(scheduleQuery)),
    error: scheduleQuery.error
      ? viewingSomeoneElse
        ? "This schedule could not be loaded."
        : "Your appointments could not be loaded."
      : null,
    throttledError: firstCalThrottled(scheduleQuery.error),
    // From the shape, so a week on its way does not flash the grid up over an onboarding gap.
    resolution: shape?.status ?? "ok",
    calUserId,
    refresh,
    isFetching: scheduleQuery.isFetching,
  }
}

/** How many of `bookings` start on each site-local day. */
function countByDay(bookings: readonly BookingDetails[], timeZone: string): Record<CalendarDate, number> {
  const counts: Record<CalendarDate, number> = {}
  for (const booking of bookings) {
    const day = zonedDateAndMinutes(booking.start, timeZone).date
    counts[day] = (counts[day] ?? 0) + 1
  }
  return counts
}
